import React, { useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { navigate } from '@reach/router';
import axios from 'axios';
import { APPS, getUrl } from '@squirrellearningltd/go-shared-utils';

import BackLink from '../../components/BackLink';
import BoomerItemPreview from './BoomerItemPreview';
import defaultBoomer from './images/default-boomer.png';
import laptop from './images/laptop.png';
import boomerItemsImage from '../../images/boomer_admin_items.png';
import plusCircled from '../../images/plus-circled.svg';
import { DEFAULT_COORDINATES, getBoomerItemCategory } from './categories';
import styles from './BoomerItems.module.scss';
import {
    CREATE_AVATAR_ITEM,
    EDIT_AVATAR_ITEM,
    GET_BOOMER_ITEMS,
    GET_S3_UPLOAD_QUERY,
} from './services/graphql';

const AddBoomerItem = ({ categoryKey, item }) => {
    const existingUrls = item ? item.url || [] : [];
    const [itemPreviews, setItemPreviews] = useState(() => existingUrls.map(url =>
        /^(https?:)?\/\//.test(url) || url.startsWith('data:') ? url :
            `${getUrl(APPS.go_portal_pupil)}${url.startsWith('/') ? '' : '/'}${url}`));
    const [itemUrls, setItemUrls] = useState(existingUrls);
    const [imagesChanged, setImagesChanged] = useState(false);
    const [publicationStatus, setPublicationStatus] = useState('false');
    const [selectedStyle, setSelectedStyle] = useState(0);
    const itemPreview = itemPreviews[selectedStyle] || '';
    const [fileError, setFileError] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [name, setName] = useState(item ? item.name || '' : '');
    const [gender, setGender] = useState(item ? ({ female: '1', male: '2', mixed: '3' }[item.gender] || '') : '');
    const [price, setPrice] = useState(item && item.price != null ? item.price : '');
    const [formError, setFormError] = useState('');
    const [itemPositions, setItemPositions] = useState(item ? { [categoryKey]: { x: item.xOffset || 0, y: item.yPos || 0 } } : {});
    const [positionInputs, setPositionInputs] = useState({});
    const client = useApolloClient();
    const category = getBoomerItemCategory(categoryKey);
    const coordinates = itemPositions[categoryKey] ||
        DEFAULT_COORDINATES[category && category.itemType];
    const previewCoordinates = coordinates || { x: 0, y: 0 };

    const moveItem = (x, y) => {
        setPositionInputs({});
        setItemPositions((positions) => {
            const current = positions[categoryKey] || previewCoordinates;
            return {
                ...positions,
                [categoryKey]: { x: current.x + x, y: current.y + y },
            };
        });
    };

    const onPositionChange = (axis, value) => {
        if (!/^-?\d*$/.test(value)) {
            return;
        }

        setPositionInputs((inputs) => ({
            ...inputs,
            [`${categoryKey}-${axis}`]: value,
        }));

        const number = Number(value);
        if (value === '' || value === '-' || number < -2147483648 || number > 2147483647) {
            return;
        }

        setItemPositions((positions) => ({
            ...positions,
            [categoryKey]: {
                ...(positions[categoryKey] || previewCoordinates),
                [axis]: number,
            },
        }));
    };


    const [createAvatarItem, { loading: creatingAvatarItem }] = useMutation(
        item ? EDIT_AVATAR_ITEM : CREATE_AVATAR_ITEM,
        {
            refetchQueries: [
                { query: GET_BOOMER_ITEMS },
                {
                    query: GET_BOOMER_ITEMS,
                    variables: { published: false },
                },
            ],
            awaitRefetchQueries: true,
            onCompleted: (data) => {
                const result = item ? data.editAvatarItem : data.createAvatarItem;

                if (result.errors && result.errors.length > 0) {
                    setFormError(result.errors[0]);
                    return;
                }

                if (result.avatarItem && result.avatarItem.id) {
                    navigate(`/boomerItems/${category.key}`, { state: { draft: Boolean(item) && !result.avatarItem.published } });
                }
            },
            onError: (error) => {
                setFormError(error.message);
            },
        }
    );

    const onSelectFile = (event) => {
        const files = Array.from(event.target.files);
        event.target.value = '';
        if (!files.length) return;

        const allowedTypes = ['jpg', 'jpeg', 'png', 'svg'];
        if (files.some(file => !allowedTypes.includes(file.name.split('.').pop().toLowerCase()))) {
            setFileError('Only .jpg, .jpeg, .png, and .svg files are allowed.');
            return;
        }

        setFileError('');
        setImagesChanged(true);
        setItemPreviews([]);
        setItemUrls([]);
        setSelectedStyle(0);
        setUploading(true);

        const previews = [];
        const urls = [];
        const uploadId = Date.now();

        const onUploadError = () => {
            setFileError('Images could not be uploaded. Please select them again.');
            setUploading(false);
        };

        // Upload in order so the first image remains the thumbnail and Style 1.
        const uploadFile = (index) => {
            if (index === files.length) {
                setItemUrls(urls);
                setUploading(false);
                return;
            }

            const file = files[index];
            const extension = file.name.split('.').pop().toLowerCase();
            client.query({
                query: GET_S3_UPLOAD_QUERY,
                variables: {
                    name: `avatarItemResources-${uploadId}-${index}.${extension}`,
                    fileType: extension,
                    object: 'avatarItemResources',
                },
            }).then((response) => {
                const uploadUrl = response.data.s3UploadUrlFor.uploadUrl;
                return axios.put(uploadUrl, file, { headers: { 'Content-Type': '' } })
                    .then(() => {
                        urls.push(uploadUrl.split('?')[0]);
                        uploadFile(index + 1);
                    });
            }).catch(onUploadError);
        };

        const readFile = (index) => {
            if (index === files.length) {
                setItemPreviews(previews);
                uploadFile(0);
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                previews.push(reader.result);
                readFile(index + 1);
            };
            reader.onerror = onUploadError;
            reader.readAsDataURL(files[index]);
        };

        readFile(0);
    };

    const downloadItem = () => {
        if (!itemPreview || downloading) return;
        setDownloading(true);
        setDownloadError('');
        const filename = `${(name.trim() || 'avatar-item').replace(/[^a-z0-9_-]/gi, '-')}-style-${selectedStyle + 1}`;
        axios({
            method: 'get',
            url: itemPreview,
            responseType: 'blob',
        }).then(response => {
            const blob = response.data;
            const extension = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/svg+xml': 'svg', 'image/webp': 'webp' }[blob.type] || '';
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = extension ? `${filename}.${extension}` : filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
            setDownloading(false);
        }).catch(() => {
            setDownloadError('Could not download the image. Please try again.');
            setDownloading(false);
        });
    };

    const onPriceChange = (event) => {
        setPrice(event.target.value);
    };

    const onNameChange = (event) => {
        setName(event.target.value);
    };

    const onGenderChange = (event) => {
        setGender(event.target.value);
    };

    const validateForm = () => {
        if (!name.trim()) {
            setFormError('Please enter an item name.');
            return false;
        }

        if (!gender) {
            setFormError('Please select a gender.');
            return false;
        }

        if (price === '') {
            setFormError('Please enter a Gem Cost.');
            return false;
        }

        if (Number(price) < 0 || !Number.isInteger(Number(price))) {
            setFormError('Gem Cost must be zero or a positive whole number.');
            return false;
        }

        if (uploading || !itemUrls.length || itemUrls.length !== itemPreviews.length) {
            setFormError('Please upload an item image.');
            return false;
        }

        setFormError('');
        return true;
    };

    const saveAvatarItem = (published) => {
        if (!validateForm()) {
            return;
        }

        let xOffset = null;
        let yPos = null;

        if (coordinates) {
            xOffset = coordinates.x;
            yPos = coordinates.y;
        }

        createAvatarItem({
            variables: {
                name: name.trim(),
                itemType: category.itemType,
                gender: Number(gender),
                ...(item ? { avatarItemId: item.id } : { accessType: 1 }),
                ...(!item || imagesChanged ? { tiles: itemUrls.length, photoUrls: itemUrls } : {}),
                yPos,
                xOffset,
                price: Number(price),
                published,
            },
        });
    };

    const publishAvatarItem = () => {
        saveAvatarItem(true);
    };

    const saveAvatarItemDraft = () => {
        saveAvatarItem(false);
    };

    const onSubmit = (event) => {
        event.preventDefault();
    };

    if (!category) {
        return (
            <>
                <div className={styles.sectionHeader}>
                    <h1 className={styles.headerTitle}>Boomer Items</h1>
                    <BackLink to='/boomerItems' text='Back' />
                </div>
                <p className={styles.statusMessage}>Category not found.</p>
            </>
        );
    }

    const isSkin = category.type === 'body';
    const isBackdrop = category.type === 'backdrop';
    const supportsStyles = !isSkin && !isBackdrop;
    const boomerImage = isSkin && itemPreview ? itemPreview : defaultBoomer;

    return (
        <>
            <BackLink to={`/boomerItems/${category.key}`} text='Back' />
            <div className={styles.sectionHeader}>
                <div className={styles.titleContainer}>
                    <h1 className={styles.headerTitle}>
                        Overview/Boomer Items/{category.name}/{item ? 'Edit' : 'New'}
                    </h1>
                    <img
                        className={styles.headerImage}
                        src={boomerItemsImage}
                        alt='Boomer Items'
                    />
                </div>
                <div className={styles.headerActions}>
                    <button
                        className={`${styles.actionButton} ${styles.publishButton}`}
                        disabled={creatingAvatarItem || uploading}
                        onClick={item ? () => saveAvatarItem(publicationStatus === 'true') : publishAvatarItem}
                        type='button'
                    >
                        {creatingAvatarItem ? 'Saving...' : item ? 'Save Changes' : 'Publish'}
                    </button>
                    <button type='button' className={`${styles.actionButton} ${styles.downloadButton}`} disabled={!itemPreview || downloading} onClick={downloadItem}>
                        {downloading ? 'Downloading...' : 'Download'}
                    </button>
                    {!item && <button
                        className={`${styles.actionButton} ${styles.draftButton}`}
                        disabled={creatingAvatarItem || uploading}
                        onClick={saveAvatarItemDraft}
                        type='button'
                    >
                        {creatingAvatarItem ? 'Saving...' : 'Save Draft'}
                    </button>}
                </div>
            </div>

            <div className={styles.addItemLayout}>
                <form className={styles.addItemForm} onSubmit={onSubmit}>
                    {item && <>
                        <h2>Status</h2>
                        <div className={`${styles.formGroup} basic-form__group`}>
                            <select className={`${styles.formInput} basic-form__text-select`} aria-label='Publication status' value={publicationStatus} onChange={event => setPublicationStatus(event.target.value)}>
                                <option value='false'>Unpublished</option>
                                <option value='true'>Published</option>
                            </select>
                        </div>
                    </>}
                    <h2>Name</h2>
                    <div
                        className={`${styles.formGroup} basic-form__group`}
                    >
                        <input
                            name='name'
                            className={`${styles.formInput} basic-form__text-box`}
                            type='text'
                            value={name}
                            onChange={onNameChange}
                            required
                        />
                    </div>

                    <h2>Gender</h2>
                    <div
                        className={`${styles.formGroup} basic-form__group`}
                    >
                        <select
                            name='gender'
                            className={`${styles.formInput} basic-form__text-select`}
                            value={gender}
                            onChange={onGenderChange}
                            required
                        >
                            <option value=''>Select gender</option>
                            <option value='1'>Female</option>
                            <option value='2'>Male</option>
                            <option value='3'>Mixed</option>
                        </select>
                    </div>

                    <h2>Gem Cost</h2>
                    <div
                        className={`${styles.formGroup} basic-form__group`}
                    >
                        <input
                            name='price'
                            className={`${styles.formInput} ${styles.priceInput} basic-form__text-box`}
                            type='number'
                            min='0'
                            step='1'
                            value={price}
                            onChange={onPriceChange}
                            required
                        />
                    </div>

                    <h2>Item</h2>
                    <div className={styles.itemUpload}>
                        <input
                            className={styles.itemFileInput}
                            onChange={onSelectFile}
                            type='file'
                            multiple={supportsStyles}
                            disabled={uploading || creatingAvatarItem}
                            aria-label='Upload item images'
                            accept='.jpg,.jpeg,.png,.svg'
                        />
                        {itemPreview ? (
                            <img
                                className={styles.uploadedItem}
                                src={itemPreview}
                                alt={`${category.itemName} preview`}
                            />
                        ) : (
                            <img
                                className={styles.uploadIcon}
                                src={plusCircled}
                                alt=''
                            />
                        )}
                    </div>
                    {supportsStyles && (
                        <p>Select images together for different styles. The first is the thumbnail. Selecting again replaces the images.</p>
                    )}
                    {itemPreviews.length > 1 && (
                        <div className={styles.stylePreviews}>
                            {itemPreviews.map((preview, index) => (
                                <button
                                    key={index}
                                    type='button'
                                    aria-pressed={selectedStyle === index}
                                    onClick={() => setSelectedStyle(index)}
                                >
                                    <img src={preview} alt='' />
                                    Style {index + 1}
                                </button>
                            ))}
                        </div>
                    )}
                    {downloadError && <p className={styles.fileError} role='alert'>{downloadError}</p>}
                    {fileError && (
                        <p className={styles.fileError}>{fileError}</p>
                    )}
                    {uploading && (
                        <p className={styles.uploadStatus}>Uploading...</p>
                    )}
                    {itemUrls.length > 0 && !uploading && (
                        <p className={styles.uploadStatus}>{itemUrls.length === 1 ? 'Image uploaded' : `${itemUrls.length} images uploaded`}</p>
                    )}
                    {formError && (
                        <p className={styles.fileError}>{formError}</p>
                    )}
                </form>

                <div className={styles.boomerPreview}>
                    {isBackdrop ? (
                        <svg className={styles.previewBackdrop} viewBox='0 0 1254 1254' role='img' aria-label='Backdrop laptop preview'>
                            <defs>
                                <clipPath id='admin-backdrop-screen'><rect x='239' y='208' width='774' height='469' rx='30' /></clipPath>
                            </defs>
                            <image href={laptop} width='1254' height='1254' />
                            {itemPreview && <image href={itemPreview} x='239' y='208' width='774' height='469' preserveAspectRatio='xMidYMid slice' clipPath='url(#admin-backdrop-screen)' />}
                        </svg>
                    ) : (
                        <BoomerItemPreview
                            className={styles.previewAvatar}
                            bodySrc={boomerImage}
                            itemSrc={!isSkin ? itemPreview : ''}
                            coordinates={previewCoordinates}
                            itemName={category.itemName}
                        />
                    )}
                    {!isSkin && !isBackdrop && itemPreview && (
                        <div className={styles.positionControls}>
                            <div className={styles.positionArrows}>
                                <button type='button' aria-label='Move item up' disabled={creatingAvatarItem} onClick={() => moveItem(0, -1)}>↑</button>
                                <button type='button' aria-label='Move item left' disabled={creatingAvatarItem} onClick={() => moveItem(-1, 0)}>←</button>
                                <button type='button' aria-label='Move item right' disabled={creatingAvatarItem} onClick={() => moveItem(1, 0)}>→</button>
                                <button type='button' aria-label='Move item down' disabled={creatingAvatarItem} onClick={() => moveItem(0, 1)}>↓</button>
                            </div>
                            <div className={styles.positionValues}>
                                {['x', 'y'].map((axis) => (
                                    <label key={axis}>
                                        {axis.toUpperCase()}:
                                        <input
                                            type='text'
                                            aria-label={`${axis.toUpperCase()} position`}
                                            value={positionInputs[`${categoryKey}-${axis}`] !== undefined
                                                ? positionInputs[`${categoryKey}-${axis}`]
                                                : previewCoordinates[axis]}
                                            disabled={creatingAvatarItem}
                                            onChange={(event) => onPositionChange(axis, event.target.value)}
                                            onBlur={() => setPositionInputs({})}
                                        />
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AddBoomerItem;
